import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, fromEvent, tap, shareReplay, finalize, throwError, takeUntil, filter } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthData } from '../models/auth.model';
import { APP_ROUTES } from '../../app.routes';
import { ACCOUNT_LOCAL_STORAGE_KEY } from '../interceptors/account.interceptor';

export const AUTH_DATA_KEY = 'authData';
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private url = environment.apiUrl;
  private refresh$: Observable<AuthData> | null = null;
  private loggedOut$ = new Subject<void>();

  constructor() {
    this.watchCrossTabLogout();
  }

  private watchCrossTabLogout(): void {
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        filter(event => event.storageArea === localStorage && event.key === AUTH_DATA_KEY && event.newValue === null)
      )
      .subscribe(() => this.logout());
  }

  public get authData(): AuthData | null {
    const auth = localStorage.getItem(AUTH_DATA_KEY);
    return auth ? JSON.parse(auth) : null;
  }

  public login(login: string, password: string) {
    return this.http
      .post<AuthData>(
        `${this.url}/Auth/Login`,
        { login, password },
        { context: new HttpContext().set(SKIP_AUTH, true) }
      )
      .pipe(tap(response => localStorage.setItem(AUTH_DATA_KEY, JSON.stringify(response))));
  }

  public refreshToken(): Observable<AuthData> {
    const authData = this.authData;
    if (!authData) {
      return throwError(() => new Error('No refresh token available'));
    }
    if (!this.refresh$) {
      this.refresh$ = this.http
        .post<AuthData>(
          `${this.url}/Auth/Refresh`,
          { token: authData.refreshToken },
          { context: new HttpContext().set(SKIP_AUTH, true) }
        )
        .pipe(
          takeUntil(this.loggedOut$),
          tap(response => localStorage.setItem(AUTH_DATA_KEY, JSON.stringify(response))),
          finalize(() => (this.refresh$ = null)),
          shareReplay(1)
        );
    }
    return this.refresh$;
  }

  public logout(): void {
    this.loggedOut$.next();
    localStorage.removeItem(AUTH_DATA_KEY);
    localStorage.removeItem(ACCOUNT_LOCAL_STORAGE_KEY);
    void this.router.navigate([APP_ROUTES.login]);
  }

  public isAuthenticated(): boolean {
    return this.authData !== null;
  }

  public isAdmin() {
    return Boolean(this.authData?.isAdmin);
  }
}
